import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { generateAvatarColor, getInitials, getContrastTextColor, ROLE_COLORS } from '@/lib/avatarColors';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  role?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  useVibrantColors?: boolean;
  className?: string;
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

export function UserAvatar({
  userId,
  firstName,
  lastName,
  avatarUrl,
  role,
  size = 'md',
  useVibrantColors = false,
  className,
}: UserAvatarProps) {
  const initials = getInitials(firstName, lastName);
  const sizeClass = sizeClasses[size];

  // Vibrant mode: unique color per user with white text
  if (useVibrantColors) {
    const bgColor = generateAvatarColor(userId);
    const textColor = getContrastTextColor(bgColor);

    return (
      <Avatar className={cn(sizeClass, className)}>
        {avatarUrl && <AvatarImage src={avatarUrl} alt={`${firstName} ${lastName}`} />}
        <AvatarFallback
          style={{ backgroundColor: bgColor, color: textColor }}
          className="font-semibold"
        >
          {initials}
        </AvatarFallback>
      </Avatar>
    );
  }

  // Pastel mode: role-based colors (existing style)
  const roleColors = role ? ROLE_COLORS[role] : undefined;
  const bgClass = roleColors?.bg || 'bg-gray-100 dark:bg-gray-800';
  const textClass = roleColors?.text || 'text-gray-600 dark:text-gray-300';

  return (
    <Avatar className={cn(sizeClass, className)}>
      {avatarUrl && <AvatarImage src={avatarUrl} alt={`${firstName} ${lastName}`} />}
      <AvatarFallback className={cn('font-medium', bgClass, textClass)}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
